import React, { useState } from 'react';
import { Dialog, DialogTitle, DialogContent, Button, Box, Typography, IconButton, Paper, alpha, useTheme } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import RedditIcon from '@mui/icons-material/Reddit';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { extractFinalMessageContent } from 'utils/exportChatDocx';

const ShareSuccessDialog = ({ open, onClose, shareUrl, threadTitle = 'Chat Thread', onMakePrivate, messages = [] }) => {
  const [copied, setCopied] = useState(false);
  const theme = useTheme();

  if (!shareUrl) return null;

  // Construct full URL
  const fullPath = `/agentic${shareUrl}`;
  const fullUrl = typeof window !== 'undefined' ? `${window.location.origin}${fullPath}` : '';

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(fullUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleShareTo = (platform) => {
    const encodedUrl = encodeURIComponent(fullUrl);
    const encodedTitle = encodeURIComponent(threadTitle);

    const urls = {
      twitter: `https://twitter.com/intent/tweet?text=${encodedTitle}&url=${encodedUrl}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
      reddit: `https://reddit.com/submit?url=${encodedUrl}&title=${encodedTitle}`
    };

    if (urls[platform]) {
      window.open(urls[platform], '_blank', 'width=600,height=400');
    }
  };

  // Get preview messages (first user message and first assistant response)
  const getPreviewMessages = () => {
    if (!messages || messages.length === 0) return { user: null, assistant: null };

    const userMessage = messages.find((m) => m.type === 'user');
    const assistantMessage = messages.find((m) => m.type === 'ai' || m.type === 'assistant');

    // Extract final message from assistant content if it contains tool calls
    if (assistantMessage && assistantMessage.content) {
      const finalContent = extractFinalMessageContent(assistantMessage.content);
      return {
        user: userMessage,
        assistant: { ...assistantMessage, content: finalContent }
      };
    }

    return { user: userMessage, assistant: assistantMessage };
  };

  const { user: previewUser, assistant: previewAssistant } = getPreviewMessages();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2
        }
      }}
    >
      <DialogTitle
        sx={{
          pb: 2,
          borderBottom: 1,
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 3,
          pt: 3
        }}
      >
        <Typography variant="h5" component="span" fontWeight={600}>
          {threadTitle}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {onMakePrivate && (
            <Button
              variant="text"
              onClick={onMakePrivate}
              sx={{
                textTransform: 'none',
                fontSize: '0.875rem',
                color: 'text.secondary',
                '&:hover': {
                  color: 'text.primary',
                  bgcolor: 'transparent'
                }
              }}
            >
              Make Private
            </Button>
          )}
          <IconButton
            size="small"
            onClick={onClose}
            sx={{
              width: 32,
              height: 32,
              borderRadius: '50%'
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ px: 3, py: 3 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Chat Preview */}
          <Paper
            variant="outlined"
            sx={{
              borderRadius: 2,
              overflow: 'hidden',
              bgcolor: (theme) => alpha(theme.palette.background.paper, 0.5)
            }}
          >
            <Box sx={{ p: 2.5, maxHeight: 400, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 2 }}>
              {/* User Message */}
              {previewUser && (
                <Box
                  sx={{
                    bgcolor: 'background.paper',
                    borderRadius: 2,
                    p: 2,
                    alignSelf: 'flex-end',
                    maxWidth: '85%'
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      lineHeight: 1.75,
                      color: 'text.primary',
                      fontSize: '0.875rem'
                    }}
                  >
                    {previewUser.content || 'User message'}
                  </Typography>
                </Box>
              )}

              {/* Thinking indicator (if available) */}
              {previewAssistant && previewAssistant.content && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 0.5 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                    Thought for 6s
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                    ›
                  </Typography>
                </Box>
              )}

              {/* Assistant Response with Markdown */}
              {previewAssistant && previewAssistant.content && (
                <Box
                  sx={{
                    px: 0.5,
                    '& .markdown-preview': {
                      fontSize: '0.875rem',
                      lineHeight: 1.75,
                      color: 'text.primary',
                      '& p': {
                        margin: 0,
                        marginBottom: '0.75rem',
                        '&:last-child': {
                          marginBottom: 0
                        }
                      },
                      '& h1, & h2, & h3, & h4, & h5, & h6': {
                        marginTop: '1rem',
                        marginBottom: '0.5rem',
                        fontWeight: 600,
                        lineHeight: 1.4
                      },
                      '& h1': { fontSize: '1.5rem' },
                      '& h2': { fontSize: '1.25rem' },
                      '& h3': { fontSize: '1.125rem' },
                      '& ul, & ol': {
                        margin: '0.5rem 0',
                        paddingLeft: '1.5rem'
                      },
                      '& li': {
                        margin: '0.25rem 0'
                      },
                      '& code': {
                        backgroundColor: alpha(theme.palette.text.primary, 0.1),
                        padding: '0.125rem 0.25rem',
                        borderRadius: '0.25rem',
                        fontSize: '0.8125rem',
                        fontFamily: 'monospace'
                      },
                      '& pre': {
                        backgroundColor: alpha(theme.palette.text.primary, 0.05),
                        padding: '0.75rem',
                        borderRadius: '0.5rem',
                        overflow: 'auto',
                        margin: '0.5rem 0',
                        '& code': {
                          backgroundColor: 'transparent',
                          padding: 0
                        }
                      },
                      '& strong': {
                        fontWeight: 600
                      },
                      '& blockquote': {
                        borderLeft: `4px solid ${theme.palette.divider}`,
                        paddingLeft: '1rem',
                        marginLeft: 0,
                        marginY: '0.5rem',
                        fontStyle: 'italic',
                        opacity: 0.9
                      },
                      '& a': {
                        color: theme.palette.primary.main,
                        textDecoration: 'none',
                        '&:hover': {
                          textDecoration: 'underline'
                        }
                      }
                    }
                  }}
                >
                  <Box className="markdown-preview">
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                      {previewAssistant.content.length > 500
                        ? `${previewAssistant.content.substring(0, 500)}...`
                        : previewAssistant.content}
                    </ReactMarkdown>
                  </Box>
                </Box>
              )}

              {/* Brand Badge */}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', pt: 1 }}>
                <Typography variant="body2" fontWeight={600} color="text.primary">
                  AskPot
                </Typography>
              </Box>
            </Box>
          </Paper>

          {/* Social Sharing Buttons */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5, pt: 1 }}>
            {/* Copy Link */}
            <Box
              component="button"
              onClick={handleCopyLink}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 1,
                p: 2,
                bgcolor: 'transparent',
                border: 'none',
                cursor: 'pointer',
                borderRadius: 2
              }}
            >
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: '50%',
                  bgcolor: 'background.paper',
                  border: 2,
                  borderColor: copied ? 'primary.main' : 'divider',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'border-color 0.2s',
                  '&:hover': {
                    borderColor: 'primary.main'
                  }
                }}
              >
                {copied ? (
                  <CheckIcon sx={{ fontSize: 20, color: 'primary.main' }} />
                ) : (
                  <ContentCopyIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                )}
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                Copy link
              </Typography>
            </Box>

            {/* X/Twitter */}
            <Box
              component="button"
              onClick={() => handleShareTo('twitter')}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 1,
                p: 2,
                bgcolor: 'transparent',
                border: 'none',
                cursor: 'pointer',
                borderRadius: 2
              }}
            >
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: '50%',
                  bgcolor: 'background.paper',
                  border: 2,
                  borderColor: 'divider',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'border-color 0.2s',
                  '&:hover': {
                    borderColor: 'primary.main'
                  }
                }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" style={{ color: 'var(--mui-palette-text-secondary)' }}>
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                </svg>
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                X
              </Typography>
            </Box>

            {/* LinkedIn */}
            <Box
              component="button"
              onClick={() => handleShareTo('linkedin')}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 1,
                p: 2,
                bgcolor: 'transparent',
                border: 'none',
                cursor: 'pointer',
                borderRadius: 2
              }}
            >
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: '50%',
                  bgcolor: 'background.paper',
                  border: 2,
                  borderColor: 'divider',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'border-color 0.2s',
                  '&:hover': {
                    borderColor: 'primary.main'
                  }
                }}
              >
                <LinkedInIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                LinkedIn
              </Typography>
            </Box>

            {/* Reddit */}
            <Box
              component="button"
              onClick={() => handleShareTo('reddit')}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 1,
                p: 2,
                bgcolor: 'transparent',
                border: 'none',
                cursor: 'pointer',
                borderRadius: 2
              }}
            >
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: '50%',
                  bgcolor: 'background.paper',
                  border: 2,
                  borderColor: 'divider',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'border-color 0.2s',
                  '&:hover': {
                    borderColor: 'primary.main'
                  }
                }}
              >
                <RedditIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                Reddit
              </Typography>
            </Box>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default ShareSuccessDialog;
